import { useState } from "react";
import { cn } from "@/utils/cn";
import { SupplierCreature } from "./gameTypes";
import { formatCurrency, statBar } from "./gameUtils";

const TIER_COLORS: Record<string, { border: string; bg: string; text: string; badge: string }> = {
  lendario: { border: "border-yellow-500", bg: "bg-yellow-950/30", text: "text-yellow-400", badge: "bg-yellow-600" },
  raro:     { border: "border-purple-500", bg: "bg-purple-950/30", text: "text-purple-400", badge: "bg-purple-600" },
  incomum:  { border: "border-blue-500",   bg: "bg-blue-950/30",   text: "text-blue-400",   badge: "bg-blue-600" },
  comum:    { border: "border-gray-700",   bg: "bg-gray-900/40",   text: "text-gray-400",   badge: "bg-gray-700" },
};

interface GamePokedexProps {
  creatures: SupplierCreature[];
}

type FilterTier = "todos" | "lendario" | "raro" | "incomum" | "comum";
type FilterCapture = "todos" | "capturado" | "pendente";

export function GamePokedex({ creatures }: GamePokedexProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterTier, setFilterTier] = useState<FilterTier>("todos");
  const [filterCapture] = useState<FilterCapture>("todos");
  const [search, setSearch] = useState("");

  const pixelFont: React.CSSProperties = { fontFamily: "'Press Start 2P', monospace" };

  const filtered = creatures.filter((c) => {
    if (filterTier !== "todos" && c.tier !== filterTier) return false;
    if (filterCapture === "capturado" && !c.isCapturado) return false;
    if (filterCapture === "pendente" && !c.hasPendingFinanceiro) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selected = creatures.find((c) => c.id === selectedId);
  const totalCapturados = creatures.filter((c) => c.isCapturado).length;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: list */}
      <div className="flex flex-col w-full md:w-1/2 lg:w-2/5 border-r border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-3 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center justify-between mb-2">
            <span className="text-emerald-500 text-[7px]" style={pixelFont}>
              📖 POKÉDEX INSUMOS
            </span>
            <span className="text-gray-500 text-[7px] font-mono">
              {totalCapturados}/{creatures.length} capturados
            </span>
          </div>

          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="buscar fornecedor..."
            className="w-full bg-gray-900 border border-gray-800 text-gray-300 text-[9px] px-2 py-1.5 outline-none focus:border-emerald-700 font-mono placeholder:text-gray-700 mb-2"
          />

          {/* Filters */}
          <div className="flex gap-1 flex-wrap">
            {(["todos", "lendario", "raro", "incomum", "comum"] as FilterTier[]).map((t) => (
              <button
                key={t}
                onClick={() => setFilterTier(t)}
                className={cn(
                  "text-[6px] px-1.5 py-0.5 border transition-colors",
                  filterTier === t
                    ? "border-emerald-500 text-emerald-400 bg-emerald-950/40"
                    : "border-gray-800 text-gray-600 hover:border-gray-600"
                )}
                style={pixelFont}
              >
                {t === "todos" ? "TODOS" : t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-gray-700 text-[9px] font-mono">
              nenhuma criatura encontrada
            </div>
          )}
          {filtered.map((c) => {
            const colors = TIER_COLORS[c.tier] ?? TIER_COLORS.comum;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id === selectedId ? null : c.id)}
                className={cn(
                  "w-full text-left p-3 border-b border-gray-900 hover:bg-gray-900/60 transition-colors flex items-center gap-3",
                  selectedId === c.id && "bg-gray-900"
                )}
              >
                {/* Sprite */}
                <div
                  className={cn(
                    "flex-shrink-0 w-10 h-10 flex items-center justify-center border text-xl",
                    c.isCapturado ? colors.border : "border-gray-800 opacity-50"
                  )}
                >
                  {c.isCapturado ? c.sprite : "?"}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className={cn(
                        "text-[6px] px-1 py-0.5",
                        colors.badge,
                        "text-white"
                      )}
                      style={pixelFont}
                    >
                      LV.{c.level}
                    </span>
                    <span className="text-[7px] text-white font-mono truncate">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[7px] font-mono" style={{ color: "inherit" }}>
                      {c.tierEmoji} {c.tierLabel}
                    </span>
                    {c.hasPendingFinanceiro && (
                      <span className="text-[6px] text-red-400 font-mono">⚠ pendente</span>
                    )}
                  </div>
                </div>

                {/* Capture status */}
                <div className="flex-shrink-0">
                  {c.isCapturado ? (
                    <span className="text-emerald-500 text-xs">✓</span>
                  ) : (
                    <span className="text-gray-700 text-xs">○</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: detail panel */}
      <div className="hidden md:flex flex-1 flex-col overflow-hidden bg-gray-950">
        {selected ? (
          <CreatureDetail creature={selected} pixelFont={pixelFont} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-700 gap-3">
            <div className="text-4xl opacity-30">📖</div>
            <span className="text-[8px] font-mono">selecione um fornecedor</span>
          </div>
        )}
      </div>
    </div>
  );
}

function CreatureDetail({
  creature: c,
  pixelFont,
}: {
  creature: SupplierCreature;
  pixelFont: React.CSSProperties;
}) {
  const colors = TIER_COLORS[c.tier] ?? TIER_COLORS.comum;

  return (
    <div className="flex-1 overflow-y-auto p-5">
      {/* Sprite + name */}
      <div className={cn("border-2 p-5 mb-4 text-center", colors.border, colors.bg)}>
        <div className="text-6xl mb-3">{c.isCapturado ? c.sprite : "❓"}</div>
        <div className={cn("text-[9px] font-bold mb-1", colors.text)} style={pixelFont}>
          {c.name}
        </div>
        <div className="flex justify-center gap-2 mt-2">
          <span
            className={cn("text-[7px] px-2 py-0.5", colors.badge, "text-white")}
            style={pixelFont}
          >
            LV.{c.level}
          </span>
          <span
            className={cn("text-[7px] px-2 py-0.5", colors.badge, "text-white")}
            style={pixelFont}
          >
            {c.tierEmoji} {c.tierLabel}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="border border-gray-800 p-3 mb-3">
        <div className="text-gray-500 text-[7px] mb-3" style={pixelFont}>
          ESTATÍSTICAS
        </div>
        {(
          [
            { label: "ATQ", value: c.stats.ataque, color: "text-red-400" },
            { label: "DEF", value: c.stats.defesa, color: "text-blue-400" },
            { label: "VEL", value: c.stats.velocidade, color: "text-yellow-400" },
          ] as const
        ).map((s) => (
          <div key={s.label} className="flex items-center gap-2 mb-2">
            <span className={cn("w-8 text-[7px] flex-shrink-0", s.color)} style={pixelFont}>
              {s.label}
            </span>
            <span className="text-gray-600 text-[7px] font-mono flex-shrink-0 w-5 text-right">
              {s.value}
            </span>
            <span className="text-emerald-600 text-[7px] font-mono flex-1 truncate">
              {statBar(s.value)}
            </span>
          </div>
        ))}
      </div>

      {/* Data */}
      <div className="border border-gray-800 p-3 space-y-2">
        <div className="text-gray-500 text-[7px] mb-2" style={pixelFont}>
          DADOS
        </div>
        {[
          { label: "Pedidos", value: String(c.ordersCount) },
          { label: "Total", value: formatCurrency(c.totalValue) },
          { label: "Ticket Médio", value: formatCurrency(c.avgOrderValue) },
          {
            label: "Status",
            value: c.isCapturado ? "✓ Capturado" : "○ Não capturado",
          },
          ...(c.hasPendingFinanceiro
            ? [{ label: "Alerta", value: "⚠ Financeiro pendente" }]
            : []),
        ].map((row) => (
          <div key={row.label} className="flex justify-between items-start gap-2">
            <span className="text-gray-600 text-[8px] font-mono flex-shrink-0">{row.label}</span>
            <span className="text-gray-300 text-[8px] font-mono text-right">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
